import { Activity, AppWindow, ChevronLeft, ChevronRight, EyeOff, Globe, Pause, Undo2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import {
  useRestoreSlotTime,
  useTimeline,
  useUsers,
  type Timeline,
  type TimelineSlot,
  type UsageItem,
} from "@/api/queries";
import { isStaff, useCurrentUser } from "@/app/auth";
import { ActivityBar } from "@/components/common/ActivityBar";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CategoryDot } from "@/components/common/Category";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { VacationBadge } from "@/components/common/VacationBadge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dateInZone,
  formatDuration,
  formatLongDate,
  formatTime,
  hourInZone,
  minutesInZone,
  percentOf,
  shiftDate,
} from "@/lib/format";
import { errorText } from "@/lib/errors";
import { cn } from "@/lib/utils";

import { ManualTimeList } from "@/features/timesheets/ManualTimeList";

import { ActivityTabs } from "./ActivityTabs";
import { ScreenshotViewer } from "./ScreenshotViewer";

export function TimelinePage() {
  const me = useCurrentUser();
  const { userId } = useParams();
  const { search } = useLocation();
  if (!userId) return <Navigate to={`/activity/${me.id}${search}`} replace />;
  return <TimelineView userId={userId} />;
}

function TimelineView({ userId }: { userId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const me = useCurrentUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = dateInZone(new Date(), me.timezone);
  const date = params.get("date") ?? today;
  const users = useUsers();
  const timeline = useTimeline(userId, date);
  const [viewer, setViewer] = useState<number | null>(null);

  const setDate = (d: string) => setParams(d === today ? {} : { date: d }, { replace: true });
  const data = timeline.data;
  const tz = data?.timezone;

  const hours = useMemo(() => (data ? groupByHour(data.slots, data.timezone) : []), [data]);
  const shots = useMemo(() => hours.flatMap((h) => h.slots).filter((s) => s.screenshots.length > 0), [hours]);

  return (
    <>
      <PageHeader
        title={t("nav.activity")}
        actions={
          <>
            {isStaff(me) && (
              <NativeSelect
                aria-label={t("timeline.employee")}
                value={userId}
                onChange={(e) =>
                  navigate(`/activity/${e.target.value}${date === today ? "" : `?date=${date}`}`)
                }
              >
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name}
                  </option>
                ))}
              </NativeSelect>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="secondary"
                size="icon"
                aria-label={t("timeline.prevDay")}
                onClick={() => setDate(shiftDate(date, -1))}
              >
                <ChevronLeft />
              </Button>
              <input
                type="date"
                aria-label={t("timeline.date")}
                value={date}
                max={today}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="h-9 rounded-lg border bg-surface px-3 font-medium shadow-card"
              />
              <Button
                variant="secondary"
                size="icon"
                aria-label={t("timeline.nextDay")}
                disabled={date >= today}
                onClick={() => setDate(shiftDate(date, 1))}
              >
                <ChevronRight />
              </Button>
            </div>
          </>
        }
      />
      <ActivityTabs />

      {timeline.isError ? (
        <Card>
          <EmptyState
            icon={Activity}
            title={t("common.error")}
            action={
              <Button variant="secondary" onClick={() => void timeline.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        </Card>
      ) : !data ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-12 w-72" />
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <UserAvatar name={data.user.full_name} className="size-10 text-sm" />
              <div>
                <div className="flex flex-wrap items-center gap-2 text-base font-semibold">
                  {data.user.full_name}
                  <VacationBadge vacation={data.user.vacation} upcoming />
                </div>
                <div className="text-muted">
                  {formatLongDate(date, lang)} · {t("timeline.timezone", { tz: data.timezone })}
                </div>
              </div>
            </div>
          </div>

          <DaySummary data={data} lang={lang} />

          {data.manual.length > 0 && (
            <Card className="overflow-hidden">
              <CardHeader className="pb-4">
                <CardTitle>{t("timeline.manualTitle")}</CardTitle>
                <CardDescription>{t("timeline.manualHint")}</CardDescription>
              </CardHeader>
              <ManualTimeList items={data.manual} timeZone={data.timezone} empty={{ title: "" }} />
            </Card>
          )}

          {data.slots.length === 0 && data.manual.length > 0 ? null : data.slots.length === 0 ? (
            <Card>
              <EmptyState
                icon={Activity}
                title={t("timeline.emptyTitle")}
                description={t("timeline.emptyDescription")}
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
              <div className="flex flex-col gap-6">
                {hours.map((h) => (
                  <HourBlock
                    key={h.hour}
                    hour={h.hour}
                    slots={h.slots}
                    data={data}
                    lang={lang}
                    onOpen={(slot) => setViewer(shots.indexOf(slot))}
                  />
                ))}
              </div>
              <AppsCard apps={data.apps} lang={lang} />
            </div>
          )}
        </div>
      )}

      {data && viewer !== null && shots[viewer] && (
        <ScreenshotViewer
          slots={shots}
          index={viewer}
          onIndexChange={setViewer}
          onClose={() => setViewer(null)}
          projects={data.projects}
          timeZone={tz}
          canDelete={data.can_delete_screenshots}
        />
      )}
    </>
  );
}

type HourGroup = { hour: number; slots: TimelineSlot[] };

function groupByHour(slots: TimelineSlot[], tz: string): HourGroup[] {
  const map = new Map<number, TimelineSlot[]>();
  for (const s of slots) {
    const h = hourInZone(s.slot_start, tz);
    map.set(h, [...(map.get(h) ?? []), s]);
  }
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([hour, list]) => ({ hour, slots: list }));
}

function DaySummary({ data, lang }: { data: Timeline; lang: string }) {
  const { t } = useTranslation();
  const tz = data.timezone;
  const projectColor = new Map(data.projects.map((p) => [p.id, p.color]));

  // Visible range: 08:00–20:00, widened to cover all tracked time.
  // Manual time may start on the previous day or end on the next one: clip it to this day.
  const dayManual = data.manual.map((m) => ({
    ...m,
    from: dateInZone(m.started_at, tz) < data.date ? `${data.date}T00:00` : m.started_at,
    to: dateInZone(new Date(Date.parse(m.ended_at) - 1), tz) > data.date ? null : m.ended_at,
  }));
  const mins = [
    ...data.segments.flatMap((s) => [minutesInZone(s.start, tz), minutesInZone(s.end, tz) || 24 * 60]),
    ...dayManual.flatMap((m) => [
      m.from.endsWith("T00:00") ? 0 : minutesInZone(m.from, tz),
      m.to ? minutesInZone(m.to, tz) || 24 * 60 : 24 * 60,
    ]),
  ];
  const from = Math.min(8, Math.floor(Math.min(...mins, 8 * 60) / 60));
  const to = Math.max(20, Math.ceil(Math.max(...mins, 20 * 60) / 60));
  const span = (to - from) * 60;
  const pos = (iso: string | null, isEnd = false) => {
    if (iso === null) return 100;
    if (iso.endsWith("T00:00")) return ((0 - from * 60) / span) * 100;
    let m = minutesInZone(iso, tz);
    if (isEnd && m === 0) m = 24 * 60;
    return ((m - from * 60) / span) * 100;
  };

  const stats = [
    [
      t("timeline.totals.tracked"),
      formatDuration(data.totals.tracked_seconds + data.totals.manual_seconds, lang),
    ],
    ...(data.totals.manual_seconds > 0
      ? [[t("timeline.totals.manual"), formatDuration(data.totals.manual_seconds, lang)] as const]
      : []),
    [t("timeline.totals.activity"), `${data.totals.activity_percent}%`],
    [
      t("timeline.totals.productive"),
      `${formatDuration(data.totals.productive_seconds, lang)} · ${percentOf(data.totals.productive_seconds, data.totals.tracked_seconds)}%`,
    ],
    [t("timeline.totals.idleDiscarded"), formatDuration(data.totals.idle_discarded_seconds, lang)],
  ] as const;

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-6">
          {stats.map(([label, value], i) => (
            <div key={label} className={cn("flex flex-col", i > 0 && "border-l pl-6")}>
              <span className="text-xs text-muted">{label}</span>
              <span className="text-xl font-semibold tabular-nums">{value}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 text-xs text-muted">
          {data.projects.map((p) => (
            <span key={p.id} className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-[3px]" style={{ backgroundColor: p.color }} />
              {p.name}
            </span>
          ))}
          {data.manual.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="manual-hatch size-2.5 rounded-[3px] bg-muted" />
              {t("timeline.legendManual")}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <span className="idle-hatch size-2.5 rounded-[3px]" />
            {t("timeline.legendIdle")}
          </span>
        </div>
      </div>
      <div
        className="relative h-7 rounded-md bg-surface-muted"
        role="img"
        aria-label={t("timeline.totals.tracked")}
      >
        {data.segments.map((s) => (
          <div
            key={s.start}
            className="absolute inset-y-0 rounded"
            style={{
              left: `${pos(s.start)}%`,
              width: `max(calc(${pos(s.end, true) - pos(s.start)}% - 2px), 2px)`,
              backgroundColor: projectColor.get(s.project_id),
            }}
            title={`${formatTime(s.start, lang, tz)} – ${formatTime(s.end, lang, tz)}`}
          />
        ))}
        {dayManual.map((m) => (
          <div
            key={m.id}
            className={cn(
              "absolute inset-y-0 rounded",
              m.status === "approved" ? "manual-hatch" : "border-2 border-dashed bg-surface/60",
            )}
            style={{
              left: `${pos(m.from)}%`,
              width: `max(calc(${pos(m.to, true) - pos(m.from)}% - 2px), 2px)`,
              ...(m.status === "approved"
                ? { backgroundColor: m.project.color }
                : { borderColor: m.project.color }),
            }}
            title={`${t(`manual.status.${m.status}`)}: ${formatTime(m.started_at, lang, tz)} – ${formatTime(m.ended_at, lang, tz)}`}
          />
        ))}
        {data.idle_periods
          .filter((p) => p.resolution === "discarded")
          .map((p) => (
            <div
              key={p.id}
              className="idle-hatch absolute inset-y-0 rounded"
              style={{
                left: `${pos(p.started_at)}%`,
                width: `${pos(p.ended_at, true) - pos(p.started_at)}%`,
              }}
            />
          ))}
      </div>
      <div className="flex text-[11px] text-muted tabular-nums">
        {Array.from({ length: to - from }, (_, i) => (
          <span key={i} className="flex-1">
            {String(from + i).padStart(2, "0")}:00
          </span>
        ))}
      </div>
    </Card>
  );
}

function HourBlock({
  hour,
  slots,
  data,
  lang,
  onOpen,
}: {
  hour: number;
  slots: TimelineSlot[];
  data: Timeline;
  lang: string;
  onOpen: (slot: TimelineSlot) => void;
}) {
  const { t } = useTranslation();
  const tz = data.timezone;
  const tracked = slots.reduce((s, x) => s + (x.deleted ? 0 : x.tracked_seconds), 0);
  const projects = new Map(data.projects.map((p) => [p.id, p]));
  const cells: (TimelineSlot | null)[] = Array.from({ length: 6 }, (_, i) => {
    return slots.find((s) => Math.floor((minutesInZone(s.slot_start, tz) % 60) / 10) === i) ?? null;
  });
  const label = (h: number) => `${String(h).padStart(2, "0")}:00`;

  return (
    <section className="@container flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 className="font-semibold tabular-nums">
          {label(hour)} – {label((hour + 1) % 24)}
        </h2>
        <div className="h-px flex-1 bg-(--border)" />
        <span className="text-xs text-muted tabular-nums">
          {t("timeline.hourTracked", { time: formatDuration(tracked, lang) })}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 @lg:grid-cols-3 @3xl:grid-cols-6">
        {cells.map((slot, i) => {
          const start = `${String(hour).padStart(2, "0")}:${i}0`;
          // Ten minutes without tracking say nothing: the cell stays empty.
          if (!slot) return null;
          if (slot.deleted) {
            return (
              <DeletedSlot
                key={slot.id}
                slot={slot}
                start={start}
                canRestore={data.can_restore_time}
                lang={lang}
                tz={tz}
                onOpen={() => onOpen(slot)}
              />
            );
          }
          const project = projects.get(slot.project_id);
          const shot = slot.screenshots[0];
          const app = slot.apps[0];
          return (
            <div
              key={slot.id}
              className="flex flex-col gap-2 rounded-[10px] border bg-surface p-2 shadow-card"
            >
              {shot ? (
                <button
                  type="button"
                  onClick={() => onOpen(slot)}
                  className="group relative block cursor-zoom-in overflow-hidden rounded-md border"
                >
                  <img
                    src={shot.thumb_url}
                    alt={t("timeline.screenshotAlt", { time: formatTime(shot.taken_at, lang, tz) })}
                    className="aspect-[16/10] w-full object-cover transition-transform group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  {slot.screenshots.length > 1 && (
                    <span className="absolute right-1.5 bottom-1.5 rounded bg-black/65 px-1.5 text-[11px] text-white">
                      ×{slot.screenshots.length}
                    </span>
                  )}
                </button>
              ) : (
                <div className="flex aspect-[16/10] items-center justify-center rounded-md bg-surface-muted text-xs text-muted">
                  {t("timeline.noScreenshot")}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: project?.color }}
                  title={project?.name}
                />
                <span className="text-xs font-medium tabular-nums">
                  {formatTime(slot.started_at, lang, tz)} – {formatTime(slot.ended_at, lang, tz)}
                </span>
              </div>
              <ActivityBar percent={slot.activity_percent} />
              <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted" title={appTitle(app)}>
                {app?.kind === "url" ? (
                  <Globe className="size-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <AppWindow className="size-3.5 shrink-0" aria-hidden="true" />
                )}
                <span className="truncate">{app ? appLabel(app) : "—"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** A slot whose time was removed together with its screenshots. */
function DeletedSlot({
  slot,
  start,
  canRestore,
  lang,
  tz,
  onOpen,
}: {
  slot: TimelineSlot;
  start: string;
  canRestore: boolean;
  lang: string;
  tz: string;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const restore = useRestoreSlotTime();
  const [confirm, setConfirm] = useState(false);
  const hint = [slot.deleted_reason, slot.deleted_by && t("timeline.deleted.by", { name: slot.deleted_by })]
    .filter(Boolean)
    .join("\n");
  // Administrators still see the image itself until the retention period ends.
  const shot = slot.screenshots[0];
  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-dashed bg-surface p-2">
      {shot ? (
        <button
          type="button"
          onClick={onOpen}
          title={hint || undefined}
          className="group relative block cursor-zoom-in overflow-hidden rounded-md border"
        >
          <img
            src={shot.thumb_url}
            alt={t("timeline.screenshotAlt", { time: formatTime(shot.taken_at, lang, tz) })}
            className="aspect-[16/10] w-full object-cover opacity-60 grayscale transition-transform group-hover:scale-[1.03]"
            loading="lazy"
          />
          <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/70 py-0.5 text-[11px] text-white">
            <EyeOff className="size-3" aria-hidden="true" />
            {t("timeline.deleted.label")}
          </span>
        </button>
      ) : (
        <div
          className="idle-hatch flex aspect-[16/10] items-center justify-center rounded-md px-2 text-center text-xs"
          title={hint || undefined}
        >
          <span className="flex items-center gap-1 font-medium">
            <EyeOff className="size-3.5 shrink-0" aria-hidden="true" />
            {t("timeline.deleted.label")}
          </span>
        </div>
      )}
      <span className="text-xs text-muted tabular-nums">{start}</span>
      {slot.deleted_reason && (
        <p className="line-clamp-2 text-xs text-muted" title={hint}>
          {slot.deleted_reason}
        </p>
      )}
      {canRestore && (
        <Button variant="secondary" size="sm" onClick={() => setConfirm(true)}>
          <Undo2 />
          {t("timeline.deleted.restore")}
        </Button>
      )}
      {confirm && (
        <ConfirmDialog
          title={t("timeline.deleted.restoreTitle")}
          description={t("timeline.deleted.restoreText")}
          confirmLabel={t("timeline.deleted.restore")}
          errorText={(e) => errorText(t, e)}
          onConfirm={() => restore.mutateAsync({ slotId: slot.id })}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}

function appLabel(a: UsageItem): string {
  if (a.kind === "url") return a.domain ?? a.url ?? a.app_name;
  return a.window_title ? `${a.app_name} — ${a.window_title}` : a.app_name;
}

function appTitle(a: UsageItem | undefined): string | undefined {
  if (!a) return undefined;
  return [a.app_name, a.window_title, a.url].filter(Boolean).join("\n");
}

function AppsCard({ apps, lang }: { apps: UsageItem[]; lang: string }) {
  const { t } = useTranslation();
  const max = apps[0]?.seconds ?? 1;
  return (
    <Card className="overflow-hidden xl:sticky xl:top-20">
      <CardHeader className="pb-4">
        <CardTitle>{t("timeline.appsTitle")}</CardTitle>
        <CardDescription>{t("timeline.totals.tracked")}</CardDescription>
      </CardHeader>
      {apps.length === 0 ? (
        <p className="px-5 pb-5 text-muted">{t("timeline.appsEmpty")}</p>
      ) : (
        <ul>
          {apps.map((a) => (
            <li
              key={`${a.kind}:${a.app_name}:${a.url ?? a.domain ?? ""}`}
              className="flex gap-2.5 border-t px-5 py-3"
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-muted">
                {a.kind === "url" ? (
                  <Globe className="size-3.5" aria-hidden="true" />
                ) : a.app_name ? (
                  <AppWindow className="size-3.5" aria-hidden="true" />
                ) : (
                  <Pause className="size-3.5" aria-hidden="true" />
                )}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <CategoryDot category={a.category} className="self-center" />
                    <span className="truncate font-medium" title={a.url ?? a.app_name}>
                      {a.kind === "url" ? (a.url ?? a.domain) : a.app_name}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium tabular-nums">
                    {formatDuration(a.seconds, lang)}
                  </span>
                </div>
                {a.window_title && (
                  <span className="truncate text-xs text-muted" title={a.window_title}>
                    {a.window_title}
                  </span>
                )}
                <div className="h-1 rounded-full bg-surface-muted">
                  <div
                    className="h-1 rounded-full bg-primary-solid"
                    style={{ width: `${Math.max(2, (a.seconds / max) * 100)}%` }}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
